/* =====================================================================
 * Typed app-event bus.
 *
 * Thin wrapper around `window.CustomEvent` so stores, side-effects and
 * UI components can publish/subscribe to a strongly-typed event map
 * without coupling directly to each other. Stores emit events here
 * (e.g. `'storage:agentFilesCreated'` after a successful upload); UI
 * components subscribe through `onAppEvent(...)` and typically trigger
 * react-query invalidation or pane refresh on the other end.
 *
 * Events live in `@auw/stores` rather than `@auw/workspace-ui` because
 * stores consume this bus internally and would otherwise depend on
 * workspace-ui, flipping the desired dependency direction.
 * ===================================================================== */

import type { FileAttachment } from '@auw/workspace-utils';
import type { SettingsTab } from './types';

type AppEventMap = {
  'app:openSettings': {
    initialTab?: SettingsTab;
    focusSection?: 'interests';
  } | undefined;
  'chat:addFiles': { files?: File[] };
  'chat:addMailAttachments': { files: FileAttachment[] };
  'workspace:itemsChanged': { source?: string };
  'agent:anchorLatestUser': undefined;
  'agent:scrollToBottom': undefined;
  'agent:scrollState': {
    isEmpty: boolean;
    isNearBottom: boolean;
    streaming: boolean;
  };
  'workspace:changeMode': { mode: string };
  'workspace:togglePageSidebar': undefined;
  'storage:agentFilesCreated': undefined;
};

export function emitAppEvent<K extends keyof AppEventMap>(
  eventName: K,
  detail?: AppEventMap[K],
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}

export function onAppEvent<K extends keyof AppEventMap>(
  eventName: K,
  handler: (detail: AppEventMap[K], event: Event) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener: EventListener = (event) => {
    const customEvent = event as CustomEvent<AppEventMap[K]>;
    handler(customEvent.detail, event);
  };
  window.addEventListener(eventName, listener);
  return () => window.removeEventListener(eventName, listener);
}

export type { AppEventMap };
